/**
 * @typedef {import('../../../types/AuthContext').default} Auth
 * @typedef {import('../../shared/PropertyService/EditablePropertyDetails').default} EditablePropertyDetails
 * @typedef {import('../../shared/PropertyService/Valuations').default} Valuations
 * @typedef {import('./FinancesProps').default} Props
 * @typedef {{[key: string]: React.ReactNode}} ComponentMap
 * @typedef {{key: string, shouldShow: (isFinances: boolean, isMarketValuation: boolean) => boolean}} ComponentRule
 */
import { Redirect } from 'react-router-dom';
import { AuthContext } from '../../../shared/AuthProvider';
import { translate } from '../../../App/Internationalization';
import { withBreakpoint } from '../../../shared/BreakpointProvider';
import { withProperty } from '../../../App/Homeowner/PropertyProvider';
import { withSettings } from '../../../shared/SettingProvider/SettingProvider';
import { withPropertyService } from '../../../service-container';
import AgentCard from '../../../App/Homeowner/shared/AgentCard';
import ComparableMarketCard from '../../../App/Homeowner/shared/ComparableMarketCard';
import captureError from '../../../utils/captureError';
import FinaceWidgets, {
	HOMEKEEPR_FINANCE_WIDGET_KEYS,
} from '../../shared/FinaceWidgets';
import {
	buildFinanceWidgetConfiguration,
	convertFinanceWidgetCentsToDollars,
} from '../../shared/FinanceWidgets/financeWidgetAttributes';
import flags from '../../../shared/Flags';
import formatAddressToString from '../../../utils/formatAddressToString';
import images from '../../../config/local/images';
import MortgageInformationCard from './MortgageInformationCard';
import paths from '../../../config/local/paths';
import PriceHistoryCard from '../../../App/Homeowner/Finances/PriceHistoryCard';
import PropertyValuation from './PropertyValuations';
import PropertyMap from '../Properties/PropertyMap';
import React from 'react';
import SeeWhatYouCanGet from '../../../App/shared/HomeValue/SeeWhatYouCanGet';
import TierGate from '../../../shared/TierGate';
import TaxHistoryCard from '../../../App/Homeowner/Finances/TaxHistoryCard';
import { toAbsoluteUrl } from '../../../utils/toAbsoluteUrl';

const homeownerFinanceWidgetKeys = [
	HOMEKEEPR_FINANCE_WIDGET_KEYS.payLumpSum,
	HOMEKEEPR_FINANCE_WIDGET_KEYS.payExtra,
	HOMEKEEPR_FINANCE_WIDGET_KEYS.howMuchPaid,
	HOMEKEEPR_FINANCE_WIDGET_KEYS.buyNewHome,
	HOMEKEEPR_FINANCE_WIDGET_KEYS.cashOutRefinance,
	HOMEKEEPR_FINANCE_WIDGET_KEYS.investInHomeImprovements,
	HOMEKEEPR_FINANCE_WIDGET_KEYS.sellAndKeepProceeds,
];

/**
 * @param {boolean} isFinances
 * @returns {boolean}
 */
function shouldShowAvms(isFinances) {
	return !isFinances;
}

/**
 * @param {boolean} isMarketValuation
 * @returns {boolean}
 */
function shouldShowMarketValue(isMarketValuation) {
	return isMarketValuation;
}

/**
 * @param {boolean} isFinances
 * @returns {boolean}
 */
function shouldShowComps(isFinances) {
	return !isFinances;
}

/**
 * @param {boolean} isMarketValuation
 * @returns {boolean}
 */
function shouldShowMortgage(isMarketValuation) {
	return !isMarketValuation;
}

/**
 * @param {boolean} isFinances
 * @param {boolean} isMarketValuation
 * @returns {boolean}
 */
function shouldShowFinanceWidgets(isFinances, isMarketValuation) {
	return isFinances && !isMarketValuation;
}

/**
 * @param {boolean} isMarketValuation
 * @returns {boolean}
 */
function shouldShowHistory(isMarketValuation) {
	return !isMarketValuation;
}

/** @type {ComponentRule[]} */
const MAIN_COMPONENT_RULES = [
	{ key: 'avms', shouldShow: (isFinances) => shouldShowAvms(isFinances) },
	{
		key: 'marketValue',
		shouldShow: (_isFinances, isMarketValuation) =>
			shouldShowMarketValue(isMarketValuation),
	},
	{ key: 'comps', shouldShow: (isFinances) => shouldShowComps(isFinances) },
	{
		key: 'mortgage',
		shouldShow: (_isFinances, isMarketValuation) =>
			shouldShowMortgage(isMarketValuation),
	},
	{
		key: 'taxHistory',
		shouldShow: (_isFinances, isMarketValuation) =>
			shouldShowHistory(isMarketValuation),
	},
	{
		key: 'priceHistory',
		shouldShow: (_isFinances, isMarketValuation) =>
			shouldShowHistory(isMarketValuation),
	},
	{
		key: 'financeWidgets',
		shouldShow: (isFinances, isMarketValuation) =>
			shouldShowFinanceWidgets(isFinances, isMarketValuation),
	},
];

/** @type {ComponentRule[]} */
const MOBILE_COMPONENT_RULES = [
	{ key: 'avms', shouldShow: (isFinances) => shouldShowAvms(isFinances) },
	{
		key: 'marketValue',
		shouldShow: (_isFinances, isMarketValuation) =>
			shouldShowMarketValue(isMarketValuation),
	},
	{ key: 'agent', shouldShow: () => true },
	{ key: 'comps', shouldShow: (isFinances) => shouldShowComps(isFinances) },
	{
		key: 'mortgage',
		shouldShow: (_isFinances, isMarketValuation) =>
			shouldShowMortgage(isMarketValuation),
	},
	{
		key: 'taxHistory',
		shouldShow: (_isFinances, isMarketValuation) =>
			shouldShowHistory(isMarketValuation),
	},
	{
		key: 'priceHistory',
		shouldShow: (_isFinances, isMarketValuation) =>
			shouldShowHistory(isMarketValuation),
	},
	{
		key: 'financeWidgets',
		shouldShow: (isFinances, isMarketValuation) =>
			shouldShowFinanceWidgets(isFinances, isMarketValuation),
	},
];

/**
 * @param {ComponentMap} components
 * @param {ComponentRule[]} rules
 * @param {boolean} isFinances
 * @param {boolean} isMarketValuation
 * @returns {React.ReactNode[]}
 */
function getComponentsByRules(
	components,
	rules,
	isFinances,
	isMarketValuation
) {
	return rules
		.filter((rule) => rule.shouldShow(isFinances, isMarketValuation))
		.map((rule) => {
			const component = components[rule.key];

			if (!React.isValidElement(component)) {
				return component;
			}

			return React.cloneElement(component, {
				key: component.key ?? rule.key,
			});
		});
}

/**
 * @param {ComponentMap} components
 * @param {boolean} isFinances
 * @param {boolean} isMarketValuation
 * @returns {React.ReactNode[]}
 */
function getMainComponents(components, isFinances, isMarketValuation) {
	return getComponentsByRules(
		components,
		MAIN_COMPONENT_RULES,
		isFinances,
		isMarketValuation
	);
}

/**
 * @param {ComponentMap} components
 * @param {boolean} isFinances
 * @param {boolean} isMarketValuation
 * @returns {React.ReactNode[]}
 */
function getMobileComponents(components, isFinances, isMarketValuation) {
	return getComponentsByRules(
		components,
		MOBILE_COMPONENT_RULES,
		isFinances,
		isMarketValuation
	);
}

/** @extends {React.Component<Props>} */
class Finances extends React.Component {
	/** @type {Auth} */
	context = /** @type {Auth} */ ({});

	/** @type {string | null} */
	#activeFinanceWidgetPropertyId = null;

	/** @param {Props} props */
	constructor(props) {
		super(props);

		this.state = {
			financeWidgetDetails: null,
			financeWidgetValuations: null,
			isGettingFinanceWidgetData: false,
			propertyImageSrc: null,
		};
	}

	async componentDidMount() {
		const propertyId = this.#getPropertyId();

		await Promise.all([
			this.setPropertyImage(),
			this.#setFinanceWidgetData(propertyId),
		]);
	}

	/** @param {Props} prevProps */
	async componentDidUpdate(prevProps) {
		if (this.#getPropertyId() !== Finances.getPropertyId(prevProps)) {
			const propertyId = this.#getPropertyId();

			await Promise.all([
				this.setPropertyImage(),
				this.#setFinanceWidgetData(propertyId),
			]);
		}
	}

	/**
	 * @param {Props} props
	 * @returns {string | undefined}
	 */
	static getPropertyId(props) {
		return (
			props.match?.params?.propertyId ??
			props.property?.selectedProperty?.id
		);
	}

	/** @returns {string | undefined} */
	#getPropertyId() {
		return Finances.getPropertyId(this.props);
	}

	async setPropertyImage() {
		const propertyId = this.#getPropertyId();

		if (!propertyId) {
			this.setState({ propertyImageSrc: null });
			return;
		}

		try {
			const imagePath = await this.props.propertyService.getDefaultImage(
				propertyId
			);
			this.setState({
				propertyImageSrc: toAbsoluteUrl(
					imagePath ?? images.homeowners.properties.streetView
				),
			});
		} catch (_error) {
			this.setState({
				propertyImageSrc: images.homeowners.properties.streetView,
			});
		}
	}

	/**
	 * @param {string | undefined} propertyId
	 * @returns {Promise<void>}
	 */
	async #setFinanceWidgetData(propertyId) {
		this.#activeFinanceWidgetPropertyId = propertyId ?? null;

		if (!propertyId) {
			this.setState({
				financeWidgetDetails: null,
				financeWidgetValuations: null,
				isGettingFinanceWidgetData: false,
			});
			return;
		}

		this.setState({
			financeWidgetDetails: null,
			financeWidgetValuations: null,
			isGettingFinanceWidgetData: true,
		});

		try {
			const [details, valuations] = await Promise.all([
				this.props.propertyService.getEditablePropertyById(propertyId),
				this.props.propertyService.getValuations(propertyId),
			]);

			if (this.#activeFinanceWidgetPropertyId !== propertyId) {
				return;
			}

			this.setState({
				financeWidgetDetails: details,
				financeWidgetValuations: valuations,
				isGettingFinanceWidgetData: false,
			});
		} catch (error) {
			captureError(error);

			if (this.#activeFinanceWidgetPropertyId !== propertyId) {
				return;
			}

			this.setState({
				financeWidgetDetails:
					this.props.property?.selectedPropertyDetails ?? null,
				financeWidgetValuations: null,
				isGettingFinanceWidgetData: false,
			});
		}
	}

	/**
	 * @param {Valuations | null} valuations
	 * @returns {number | null}
	 */
	static #getEstimatedHomeValue(valuations) {
		if (!valuations) {
			return null;
		}

		const valuationList = [...valuations.values()]
			.flat()
			.filter((valuation) => valuation?.valueMean);

		if (!valuationList.length) {
			return null;
		}

		const latestValuation = valuationList
			.filter((valuation) => valuation.date !== null)
			.sort(
				(a, b) => (b.date?.valueOf() ?? 0) - (a.date?.valueOf() ?? 0)
			)[0];
		const valuation = latestValuation ?? valuationList[0];

		return convertFinanceWidgetCentsToDollars(valuation.valueMean);
	}

	/**
	 * @returns {JSX.Element | null}
	 */
	#renderFinanceWidgets() {
		if (this.state.isGettingFinanceWidgetData) {
			return null;
		}

		/** @type {EditablePropertyDetails | null} */
		const propertyDetails =
			this.state.financeWidgetDetails ??
			this.props.property?.selectedPropertyDetails ??
			null;
		const details = propertyDetails?.details;

		if (!details) {
			return null;
		}

		const estimatedHomeValue = Finances.#getEstimatedHomeValue(
			this.state.financeWidgetValuations
		);
		const configuration = buildFinanceWidgetConfiguration({
			accessToken: this.context.accessToken,
			agentId: this.props.property?.agentId,
			details,
			estimatedHomeValue,
			userId: this.context.userId,
			userType: 'homeowner',
		});

		return (
			<FinaceWidgets
				attributes={configuration.attributes}
				className="mt-10"
				reloadKey={`${this.#getPropertyId()}:${
					configuration.reloadKey
				}`}
				title="Finance Tools"
				widgetKeys={homeownerFinanceWidgetKeys}
			/>
		);
	}

	#renderPropertyCard() {
		const propertyId = this.#getPropertyId();
		const selectedProperty =
			this.props.property?.propertyList?.find(
				(property) => property.id === propertyId
			) ?? this.props.property?.selectedProperty;

		if (!selectedProperty?.address) {
			return null;
		}

		return (
			<section className="card card-property-detail card-border mb-6">
				<figure>
					<img
						alt="Property"
						className="image rounded w-full"
						src={
							this.state.propertyImageSrc ??
							images.homeowners.properties.streetView
						}
					/>
				</figure>
				<section className="card-body property-details text-start">
					<div className="property-detail-item width-full">
						<header className="property-detail-item-header">
							{'Address'}
						</header>
						<footer className="property-detail-item-footer">
							{formatAddressToString(selectedProperty.address)}
						</footer>
					</div>
				</section>
				<PropertyMap
					address={selectedProperty.address}
					className="property-map mb-0"
				/>
			</section>
		);
	}

	/**
	 * @param {ComponentMap} components
	 * @param {boolean} isFinances
	 * @param {boolean} isMarketValuation
	 * @returns {JSX.Element}
	 */
	static renderMainSection(components, isFinances, isMarketValuation) {
		return (
			<section className="responsive-main app-block-list">
				{getMainComponents(components, isFinances, isMarketValuation)}
			</section>
		);
	}

	/**
	 * @param {ComponentMap} components
	 * @returns {JSX.Element}
	 */
	renderSecondarySection(components) {
		return (
			<section className="responsive-secondary app-block-list">
				{this.#renderPropertyCard()}
				{components.agent}
			</section>
		);
	}

	/**
	 * @param {ComponentMap} components
	 * @param {boolean} isFinances
	 * @param {boolean} isMarketValuation
	 * @param {string} title
	 * @returns {JSX.Element}
	 */
	renderMobileView(components, isFinances, isMarketValuation, title) {
		return (
			<React.Fragment>
				<h1 className="hl-ms-4 my-3">{title}</h1>
				{this.#renderPropertyCard()}
				<section className="app-block-list">
					{getMobileComponents(
						components,
						isFinances,
						isMarketValuation
					)}
				</section>
			</React.Fragment>
		);
	}

	/**  @returns {JSX.Element} */
	render() {
		if (
			!this.props.settings.isEnabled(
				flags.homeowner.financesPage,
				'homeowner_tier'
			)
		) {
			return <Redirect to={paths.app.homeowner.dashboard} />;
		}

		const HomeownerPropertyValuation =
			/** @type {React.ComponentType<any>} */ (PropertyValuation);
		const HomeownerMarketValue = /** @type {React.ComponentType<any>} */ (
			SeeWhatYouCanGet
		);
		const HomeownerMortgageInformation =
			/** @type {React.ComponentType<any>} */ (MortgageInformationCard);
		const HomeownerPriceHistory = /** @type {React.ComponentType<any>} */ (
			PriceHistoryCard
		);
		const HomeownerTaxHistory = /** @type {React.ComponentType<any>} */ (
			TaxHistoryCard
		);

		const components = {
			agent: (
				<TierGate
					fallback={null}
					flag={flags.branding.homeownerFinances}
					userTier="homeowner_tier"
				>
					<AgentCard key="homeowner-dashboard-agent" />
				</TierGate>
			),
			avms: (
				<HomeownerPropertyValuation
					key="homeowner-dashboard-avms"
					match={this.props.match}
				/>
			),
			comps: (
				<TierGate
					fallback={null}
					flag={flags.homeowner.comparableMarketActivity}
					userTier="homeowner_tier"
				>
					<ComparableMarketCard key="homeowner-dashboard-comps" />
				</TierGate>
			),
			financeWidgets: this.#renderFinanceWidgets(),
			marketValue: (
				<HomeownerMarketValue
					key="homeowner-dashboard-marketValue"
					match={this.props.match}
				/>
			),
			mortgage: (
				<TierGate
					fallback={null}
					flag={flags.homeowner.mortgageInformation}
					userTier="homeowner_tier"
				>
					<HomeownerMortgageInformation
						key="homeowner-dashboard-mortgage"
						match={this.props.match}
					/>
				</TierGate>
			),
			priceHistory: (
				<HomeownerPriceHistory
					key="homeowner-dashboard-priceHistory"
					match={this.props.match}
				/>
			),
			taxHistory: (
				<HomeownerTaxHistory
					key="homeowner-dashboard-taxHistory"
					match={this.props.match}
				/>
			),
		};

		const title =
			this.props.contentTitle ??
			translate('homeowner.pages.finances.title');
		const isMarketValuation = this.props.mode === 'marketValuation';
		const isFinances = this.props.mode === 'finances';

		if (this.props.isBelowBreakpoint) {
			return this.renderMobileView(
				components,
				isFinances,
				isMarketValuation,
				title
			);
		}

		return (
			<React.Fragment>
				<h1 className="hl-ms-4 my-3">{title}</h1>

				<section className="app-responsive-area app-responsive-sidebar-grid">
					{Finances.renderMainSection(
						components,
						isFinances,
						isMarketValuation
					)}
					{this.renderSecondarySection(components)}
				</section>
			</React.Fragment>
		);
	}
}

Finances.contextType = AuthContext;

export default withPropertyService(
	withBreakpoint(withSettings(withProperty(Finances)))
);
